import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingIcons from 'react-loading-icons';
import Modal from './Modal';
import useJobMonitor from '../hooks/useJobMonitor';
import { addPage, addDocument, getDocumentPages } from '../utils/Api';
import { UploadCloud, AlertTriangle, CheckCircle2, FileIcon } from 'lucide-react';

const DocumentUpload = ({ isOpen, setIsOpen, mode = 'create', documentID: existingDocumentID = null }) => {
    const [statusText, setStatusText] = useState("Create Document");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [images, setImages] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);

    const [documentName, setDocumentName] = useState("");
    const [insertMode, setInsertMode] = useState('end');
    const [insertPageNum, setInsertPageNum] = useState(1);

    const [processedDocumentID, setProcessedDocumentID] = useState(null);
    const [existingCompletePages, setExistingCompletepages] = useState(null);
    const [isComplete, setIsComplete] = useState(false);

    const navigate = useNavigate();

    const {
        status: documentStatus,
        error: socketError,
    } = useJobMonitor(processedDocumentID, !!processedDocumentID);

    useEffect(() => {
        if (isOpen) {
            setStatusText(mode === 'create' ? "Create Document" : "Add Pages");
            setUploadProgress(0);
            setIsSubmitting(false);
            setImages(null);
            setError(null);
            setDocumentName("");
            setInsertMode('end');
            setInsertPageNum(1);
            setProcessedDocumentID(null);
            setIsComplete(false);
        }
    }, [isOpen, mode]);

    useEffect(() => {
        if (socketError) {
            setStatusText(`Error: ${socketError}`);
            setIsSubmitting(false);
            setProcessedDocumentID(null);
            return;
        }
        if (!processedDocumentID || !images) return;

        const totalPages = images.length;
        if (totalPages === 0) {
            setUploadProgress(0);
            return;
        }

        const statusValues = {
            "PageStatus.PENDING": 0,
            "PageStatus.EXTRACTING": 1,
            "PageStatus.STRUCTURING": 2,
            "PageStatus.COMPLETE": 3,
            "PageStatus.FAILED": 3
        };
        const maxTotalProgress = totalPages * 3;

        const currentTotalProgress = Object.values(documentStatus).reduce((accumulator, currentStatus) => {
            return accumulator + (statusValues[currentStatus] || 0);
        }, 0) - 3 * existingCompletePages;

        const progressPercentage = Math.round((currentTotalProgress / maxTotalProgress) * 100);
        setUploadProgress(Math.min(progressPercentage, 100));

        if (currentTotalProgress >= maxTotalProgress){
            setStatusText("Complete");
            setIsSubmitting(false);
            setIsComplete(true);
        }
    }, [documentStatus, socketError, processedDocumentID, images, mode]);

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const files = Array.from(event.target.files);
            setImages(files);
            setError(null);

            if (!documentName) {
                const fileName = files[0].name;
                setDocumentName(fileName.substring(0, fileName.lastIndexOf('.')) || fileName);
            }

            setStatusText("Submit");
            setProcessedDocumentID(null);
            setIsSubmitting(false);
            setUploadProgress(0);
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!images || isSubmitting) return;

        setIsSubmitting(true);
        setError(null);
        setStatusText("Initiating File Transfer");
        setUploadProgress(0);
        setProcessedDocumentID(null);
        
        try {
            let docIdToUploadTo = existingDocumentID;
            if (mode === 'create') {
                if (!documentName.trim()) {
                   setError("Document name is required.");
                   setIsSubmitting(false);
                   return;
                }
                const response = await addDocument(documentName);
                docIdToUploadTo = response.data.id;
                setProcessedDocumentID(docIdToUploadTo);
                setExistingCompletepages(0);
                console.log(`Created document with ID ${docIdToUploadTo}`);
            } else {
                setProcessedDocumentID(existingDocumentID);
                const { data: existingPages } = getDocumentPages(existingDocumentID);
                setExistingCompletepages(Object.keys(existingPages).length);
            }

            
            
            for (let i = 0; i < images.length; i++) {
                setStatusText(`Uploading pages ${i+1} of ${images.length}`);
                await addPage(docIdToUploadTo, images[i]);
            }

        } catch (error) {
            console.error("Upload Error:", error);
            setError(`Upload Failed: ${error.message}`);
            setIsSubmitting(false);
            setProcessedDocumentID(null);
        }
    };

    const handleCompletionClick = () => {
        if (processedDocumentID) {
            navigate(`/view/${processedDocumentID}`);
            setIsOpen(false);
        }
    };

    const modalTitle = mode === 'create' ? "Create New Document" : "Add Pages to Document";
    const submitButtonText = statusText;

    return (
        <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
            <h1 className="text-center text-2xl font-bold text-white mb-2">{modalTitle}</h1>
            <p className="text-center text-gray-400 mb-6">Upload images to get started.</p>
            
            <form onSubmit={handleSubmit}>
                <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1 flex flex-col items-center justify-center bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 text-center">
                        <UploadCloud className="text-sky-400 mx-auto mb-3" size={48} />
                        <label htmlFor="file-upload" className="font-semibold text-sky-400 cursor-pointer hover:text-sky-300 transition-colors">
                            Choose files
                        </label>
                        <span className="text-gray-400 text-sm"> or drag and drop</span>
                        <input id="file-upload" type="file" multiple className="sr-only" onChange={handleFileChange} accept="image/*" />
                        <p className="text-xs text-gray-500 mt-3">PNG, JPG, TIFF</p>
                        {images && (
                            <div className="mt-4 text-left w-full">
                                <h3 className="font-semibold text-sm text-white mb-2">{images.length} file(s) selected:</h3>
                                <ul className="max-h-24 overflow-y-auto space-y-1 pr-2 no-scrollbar">
                                    {images.map(file => (
                                        <li key={file.name} className="flex items-center gap-2 text-xs text-gray-300">
                                            <FileIcon size={14} className="flex-shrink-0" />
                                            <span className="truncate">{file.name}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 flex flex-col gap-4 justify-between">
                        <div>
                            {mode === 'create' && (
                                <div>
                                    <label htmlFor="doc-name" className="block text-sm font-medium text-gray-300 mb-2">Document Name</label>
                                    <input
                                        id="doc-name"
                                        type="text"
                                        placeholder="Document Name"
                                        value={documentName}
                                        onChange={(e) => setDocumentName(e.target.value)}
                                        required
                                        className="w-full border border-gray-600 text-white text-sm rounded-lg p-2.5 bg-gray-700 focus:ring-sky-500 focus:border-sky-500"
                                    />
                                </div>
                            )}

                            {mode === 'add' && (
                                <div className="w-full">
                                    <h2 className="text-sm font-medium text-gray-300 mb-2">Insertion Point</h2>
                                    <div className="flex flex-col space-y-2">
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input type="radio" name="insert-mode" value="start" checked={insertMode === 'start'} onChange={(e) => setInsertMode(e.target.value)} className="form-radio text-sky-600 bg-gray-700 border-gray-600" />
                                            <span className="text-sm text-gray-300">At the beginning</span>
                                        </label>
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input type="radio" name="insert-mode" value="after" checked={insertMode === 'after'} onChange={(e) => setInsertMode(e.target.value)} className="form-radio text-sky-600 bg-gray-700 border-gray-600" />
                                            <span className="text-sm text-gray-300">After page</span>
                                            <input
                                                type="number"
                                                value={insertPageNum}
                                                onChange={(e) => setInsertPageNum(parseInt(e.target.value, 10))}
                                                disabled={insertMode !== 'after'}
                                                min="1"
                                                className="w-16 p-1 text-sm border text-white bg-gray-700 border-gray-600 rounded-md disabled:bg-gray-800 disabled:text-gray-500 focus:ring-sky-500 focus:border-sky-500"
                                            />
                                        </label>
                                        <label className="flex items-center space-x-2 cursor-pointer">
                                            <input type="radio" name="insert-mode" value="end" checked={insertMode === 'end'} onChange={(e) => setInsertMode(e.target.value)} className="form-radio text-sky-600 bg-gray-700 border-gray-600" />
                                            <span className="text-sm text-gray-300">At the end</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="space-y-4">
                           {error && (
                                <div className="flex items-center gap-2 bg-red-500/20 text-red-400 text-sm p-3 rounded-lg">
                                    <AlertTriangle size={20} />
                                    <span>{error}</span>
                                </div>
                            )}
                            <button 
                                type={isComplete ? "button" : "submit"}
                                onClick={isComplete ? handleCompletionClick : undefined}
                                disabled={!images || isSubmitting}
                                className="w-full text-white font-semibold rounded-lg px-5 py-3 duration-150 flex flex-row justify-center items-center gap-3 h-12 bg-sky-600 hover:bg-sky-700 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all"
                            >
                                {isSubmitting ? <LoadingIcons.Grid height="2em" stroke="currentColor" /> : (isComplete ? <CheckCircle2/> : null)}
                                <span>{submitButtonText}</span>
                            </button>

                            <div className="w-full bg-gray-600 rounded-full h-2.5">
                                <div
                                    className="bg-sky-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                                    style={{ width: `${uploadProgress}%` }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    )
}

export default DocumentUpload